import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Icon,
  Space,
  Spin,
  Statistic,
  Table,
  Text,
  Textarea,
  Upload,
  useToast,
} from "@app/ui";
import { importProductsCsv } from "../api/dpp.js";
import { excelToImportCsv } from "../dpp/excel-import.js";
import { getSchemaDef } from "../dpp/schema-registry.js";
import { useT } from "../i18n.js";
import { ErrorAlert } from "../errors/index.js";
import { NoOrgState } from "../orgs/NoOrgState.js";
import { useOrgs } from "../orgs/OrgContext.js";
import type { ImportResult } from "../types.js";

/**
 * CSV import (import:run). Paste or upload a file; the API reports per-row errors.
 * An IDPP Excel data template is converted to the same CSV in the browser, so
 * the user sees exactly what gets imported and errors map back to sheet names.
 */
export function ImportPage() {
  const t = useT();
  const toast = useToast();
  const { org, loading: orgsLoading } = useOrgs();

  const [csv, setCsv] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  // Sheet name per CSV data line while the textarea still holds an Excel conversion.
  const [rowSheets, setRowSheets] = useState<string[] | null>(null);

  if (orgsLoading) return <Spin />;
  if (!org) return <NoOrgState />;

  const onFiles = (files: File[]) => {
    const file = files[files.length - 1];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      setRowSheets(null);
      void file.text().then(setCsv);
      return;
    }
    void file
      .arrayBuffer()
      .then((buf) => excelToImportCsv(buf, getSchemaDef))
      .then((res) => {
        if (res.rowSheets.length === 0) {
          toast.error(t("import.xlsxEmpty"));
          return;
        }
        setCsv(res.csv);
        setRowSheets(res.rowSheets);
        toast.success(t("import.xlsxLoaded", { n: res.rowSheets.length }));
      })
      .catch(setError);
  };

  const run = async () => {
    setError(null);
    setResult(null);
    setRunning(true);
    try {
      const res = await importProductsCsv(org.id, csv);
      setResult(res);
      toast.success(t("import.done"));
    } catch (e) {
      setError(e);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Space direction="vertical" size="lg" style={{ width: "100%" }}>
      <Card title={t("import.title")}>
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          <Alert type="info">{t("import.hint")}</Alert>
          <Textarea
            label={t("import.pasteLabel")}
            rows={10}
            value={csv}
            onChange={(e) => {
              setCsv(e.target.value);
              setRowSheets(null); // hand edits may shift lines; stop claiming sheet names
            }}
            placeholder={"sku,name,schemaId,identity.brand\nTS-001,Basic Tee,textile-v1,Acme"}
            style={{ fontFamily: "var(--az-font-mono, monospace)" }}
          />
          <Upload accept=".csv,text/csv,.xlsx" maxCount={1} onChange={onFiles}>
            {t("import.fileLabel")}
          </Upload>
          <div>
            <Button
              variant="primary"
              icon={<Icon name="upload" size={14} />}
              loading={running}
              disabled={csv.trim() === ""}
              onClick={() => void run()}
            >
              {t("import.run")}
            </Button>
          </div>
          {error != null && <ErrorAlert error={error} onRetry={() => void run()} />}
        </Space>
      </Card>

      {result && (
        <Card title={t("import.done")}>
          <Space size="lg" wrap>
            <Statistic title={t("import.created")} value={result.created} />
            <Statistic title={t("import.updated")} value={result.updated} />
            <Statistic title={t("import.errorCount")} value={result.errors.length} />
          </Space>
          {result.errors.length > 0 && (
            <div style={{ marginTop: "var(--az-sp-4)" }}>
              <Text small secondary>
                {t("import.errorsTitle")}
              </Text>
              <Table
                rowKey={(e) => String(e.row)}
                columns={[
                  rowSheets
                    ? { key: "sheet", title: t("import.sheet"), render: (e) => rowSheets[e.row - 2] ?? e.row }
                    : { key: "row", title: t("import.row"), render: (e) => e.row, numeric: true },
                  { key: "message", title: t("import.message"), render: (e) => e.message },
                ]}
                data={result.errors}
              />
            </div>
          )}
        </Card>
      )}
    </Space>
  );
}
